import React from 'react';
import { Bell, BellOff, X } from 'lucide-react';
import Card from '../components/ui/Card';
import { useLanguage } from '../lang/LangContext';
import { KEYS } from '../lang/keys';
import { usePushNotifications } from '../hooks/usePushNotifications';
import {
    useNotifications,
    useMarkNotificationRead,
    useMarkAllNotificationsRead,
    useDeleteNotification,
    useClearNotifications,
} from '../hooks/useNotifications';

const NotificationCard = ({ notification, onMarkRead, onDelete }) => {
    const Icon = notification.icon;

    return (
        <div
            className={`
                flex items-start gap-4 p-4 rounded-lg border border-surface-border 
                border-l-4 ${notification.borderColor}
                ${notification.read ? 'bg-surface-raised opacity-70' : 'bg-surface-raised'}
                transition-opacity
            `}
        >
            <div className={`mt-0.5 shrink-0 ${notification.iconColor}`}>
                <Icon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <p className={`text-sm font-medium ${notification.read ? 'text-text-secondary' : 'text-text-primary'}`}>
                            {notification.title}
                        </p>
                        <p className="text-sm text-text-muted mt-0.5">
                            {notification.body}
                        </p>
                    </div>
                    <span className="text-xs text-text-muted shrink-0 mt-0.5">{notification.time}</span>
                </div>
            </div>
            <div className="shrink-0 mt-0.5 flex items-center gap-1.5">
                {!notification.read && (
                    <button
                        onClick={() => onMarkRead(notification.uid)}
                        className="w-2 h-2 rounded-full bg-accent cursor-pointer hover:bg-accent-hover transition-colors"
                        title="Mark as read"
                        aria-label="Mark as read"
                    />
                )}
                <button
                    onClick={() => onDelete(notification.uid)}
                    className="p-0.5 rounded text-text-muted hover:text-text-secondary transition-colors cursor-pointer"
                    aria-label="Delete notification"
                >
                    <X className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );
};

const Notifications = () => {
    const { t } = useLanguage();
    const { isSupported, isSubscribed, isLoading: isPushLoading, error: pushError, subscribe, unsubscribe } = usePushNotifications();

    const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = useNotifications();
    const markRead = useMarkNotificationRead();
    const markAllRead = useMarkAllNotificationsRead();
    const deleteNotification = useDeleteNotification();
    const clearNotifications = useClearNotifications();

    const notifications = data?.pages.flatMap((p) => p.items) ?? [];
    const unreadCount = notifications.filter((n) => !n.read).length;
    const hasRead = notifications.some((n) => n.read);

    return (
        <div className="space-y-5">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-semibold text-text-primary flex items-center gap-2">
                        {t(KEYS.NOTIFICATIONS_TITLE)}
                        {unreadCount > 0 && (
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-accent text-surface-base text-[10px] font-semibold">
                                {unreadCount}
                            </span>
                        )}
                    </h1>
                    <p className="text-sm text-text-muted mt-0.5">
                        {t(KEYS.NOTIFICATIONS_SUBTITLE)}
                    </p>
                </div>
                {(unreadCount > 0 || hasRead) && (
                    <div className="flex items-center gap-3">
                        {unreadCount > 0 && (
                            <button
                                onClick={() => markAllRead.mutate()}
                                disabled={markAllRead.isPending}
                                className="text-sm text-accent hover:text-accent-hover transition-colors font-medium disabled:opacity-50"
                            >
                                {t(KEYS.NOTIFICATIONS_MARK_ALL_READ)}
                            </button>
                        )}
                        {hasRead && (
                            <button
                                onClick={() => clearNotifications.mutate('read')}
                                disabled={clearNotifications.isPending}
                                className="text-sm text-text-muted hover:text-text-secondary transition-colors disabled:opacity-40"
                            >
                                {t(KEYS.NOTIFICATIONS_CLEAR_READ)}
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Push notification settings */}
            <Card>
                <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                        <div className="mt-0.5 p-2 rounded-md bg-surface-overlay border border-surface-border shrink-0">
                            {isSubscribed ? (
                                <Bell className="w-4 h-4 text-accent" />
                            ) : (
                                <BellOff className="w-4 h-4 text-text-muted" />
                            )}
                        </div>
                        <div>
                            <p className="text-sm font-medium text-text-primary">{t(KEYS.NOTIFICATIONS_PUSH_TITLE)}</p>
                            <p className="text-sm text-text-muted mt-0.5">{t(KEYS.NOTIFICATIONS_PUSH_DESCRIPTION)}</p>
                            {!isSupported && (
                                <p className="text-xs text-text-muted mt-1.5">{t(KEYS.NOTIFICATIONS_PUSH_NOT_SUPPORTED)}</p>
                            )}
                            {pushError === 'permission_denied' && (
                                <p className="text-xs text-status-overdue mt-1.5">{t(KEYS.NOTIFICATIONS_PUSH_PERMISSION_DENIED)}</p>
                            )}
                            {pushError && pushError !== 'permission_denied' && (
                                <p className="text-xs text-status-overdue mt-1.5">{t(KEYS.NOTIFICATIONS_PUSH_ERROR)}</p>
                            )}
                        </div>
                    </div>
                    {isSupported && (
                        <button
                            onClick={isSubscribed ? unsubscribe : subscribe}
                            disabled={isPushLoading}
                            className={`
                                shrink-0 relative inline-flex items-center h-5 w-9 rounded-full transition-colors duration-200 cursor-pointer
                                focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40
                                disabled:opacity-50 disabled:cursor-not-allowed
                                ${isSubscribed ? 'bg-accent' : 'bg-surface-overlay border border-surface-border'}
                            `}
                            role="switch"
                            aria-checked={isSubscribed}
                            aria-label={isSubscribed ? t(KEYS.NOTIFICATIONS_PUSH_DISABLE) : t(KEYS.NOTIFICATIONS_PUSH_ENABLE)}
                        >
                            <span
                                className={`
                                    inline-block w-3.5 h-3.5 rounded-full bg-white shadow transition-transform duration-200
                                    ${isSubscribed ? 'translate-x-4' : 'translate-x-0.5'}
                                `}
                            />
                        </button>
                    )}
                </div>
            </Card>

            {/* Notification list */}
            <Card padding="none" className="overflow-hidden">
                <div className="divide-y divide-surface-border">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-12 px-6">
                            <p className="text-sm text-text-muted">{t(KEYS.COMMON_LOADING)}</p>
                        </div>
                    ) : isError ? (
                        <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                            <p className="text-sm text-text-secondary">{t(KEYS.NOTIFICATIONS_ERROR_LOAD)}</p>
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                            <Bell className="w-10 h-10 text-text-muted mb-4" strokeWidth={1.5} />
                            <p className="text-sm text-text-secondary">{t(KEYS.NOTIFICATIONS_EMPTY)}</p>
                        </div>
                    ) : (
                        <div className="p-3 space-y-2">
                            {notifications.map((notification) => (
                                <NotificationCard
                                    key={notification.uid}
                                    notification={notification}
                                    onMarkRead={(uid) => markRead.mutate(uid)}
                                    onDelete={(uid) => deleteNotification.mutate(uid)}
                                />
                            ))}
                            {hasNextPage && (
                                <div className="pt-1 flex justify-center">
                                    <button
                                        onClick={() => fetchNextPage()}
                                        disabled={isFetchingNextPage}
                                        className="text-sm text-accent hover:text-accent-hover transition-colors disabled:opacity-50"
                                    >
                                        {isFetchingNextPage ? t(KEYS.COMMON_LOADING) : t(KEYS.COMMON_LOAD_MORE)}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Card>
        </div>
    );
};

export default Notifications;
